import React from "react";
import { Label } from "./label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import { HelpCircle } from "lucide-react";

/**
 * Label component with integrated tooltip
 * Shows help icon that displays tooltip on hover
 * Supports both string tooltips and rich tooltip objects with title, content, and example
 * Can accept label as prop or as children
 */
export const TooltipLabel = ({ htmlFor, label, children, tooltip, className = "", asChild = false }) => {
  const labelText = label || children;
  
  if (!tooltip) {
    return asChild ? labelText : <Label htmlFor={htmlFor} className={className}>{labelText}</Label>;
  }

  // Format tooltip content - handle both string and object formats
  const formatTooltipContent = (tooltipData) => {
    // If it's already a string, return as is
    if (typeof tooltipData === 'string') {
      return <p className="text-sm">{tooltipData}</p>;
    }
    
    // If it's an object with title, content, example
    if (tooltipData && typeof tooltipData === 'object') {
      return (
        <div className="space-y-2">
          {tooltipData.title && (
            <div className="font-semibold text-sm border-b border-slate-300 pb-1">
              {tooltipData.title}
            </div>
          )}
          {tooltipData.content && (
            <p className="text-sm text-slate-200">
              {tooltipData.content}
            </p>
          )}
          {tooltipData.example && (
            <div className="mt-2 pt-2 border-t border-slate-700">
              <p className="text-xs text-slate-300 font-medium mb-1">Example:</p>
              <p className="text-xs text-slate-400 italic">
                {tooltipData.example}
              </p>
            </div>
          )}
        </div>
      );
    }
    
    return <p className="text-sm">{String(tooltipData)}</p>;
  };

  const tooltipContent = formatTooltipContent(tooltip);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {asChild ? (
            <div className="inline-flex items-center cursor-help">
              {labelText}
              <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
            </div>
          ) : (
            <Label htmlFor={htmlFor} className={`flex items-center cursor-help ${className}`}>
              {labelText}
              <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
            </Label>
          )}
        </TooltipTrigger>
        <TooltipContent className="max-w-md">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TooltipLabel;
